import React, { useState, useEffect, useCallback, useRef } from 'react';
import './AdminPage.css';

const API = 'https://roulette-api.rushelsilvester.workers.dev';
const DEALER_APP_URL = window.location.origin; // same origin: /admin and / share localStorage

const PHASE_LABELS = {
  waiting:  'Wartend',
  betting:  'Wetten läuft',
  spinning: 'Dreht',
  result:   'Ergebnis',
  closed:   'Geschlossen',
};
const PHASE_COLORS = {
  waiting:  '#666',
  betting:  '#4caf50',
  spinning: '#ff9800',
  result:   '#2196f3',
  closed:   '#333',
};
const LEVEL_LABELS = { simple: 'Einfach', medium: 'Mittel', expert: 'Expert' };

function fmt(n) { return n >= 0 ? `+${n}` : `${n}`; }
function timeAgo(ts) {
  if (!ts) return '—';
  const d = Math.floor((Date.now() - ts) / 1000);
  if (d < 60) return `${d}s`;
  if (d < 3600) return `${Math.floor(d / 60)}m`;
  return `${Math.floor(d / 3600)}h ${Math.floor((d % 3600) / 60)}m`;
}

function adminFetch(path, method = 'GET', body) {
  return fetch(`${API}${path}`, {
    method,
    headers: {
      'Authorization': 'Bearer Fifastreet09',
      ...(body ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  }).then(r => r.json());
}

export default function AdminPage() {
  const [authed, setAuthed] = useState(() => sessionStorage.getItem('admin_auth') === '1');
  const [pw, setPw] = useState('');
  const [pwError, setPwError] = useState('');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState('');
  const [lastRefresh, setLastRefresh] = useState(null);
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);

  const [expanded, setExpanded] = useState({});
  const [showHistory, setShowHistory] = useState({});
  const [addForm, setAddForm] = useState({});
  const [adjustForm, setAdjustForm] = useState({}); // { [playerId]: { open, amount, loading, error } }
  const [actionLoading, setActionLoading] = useState({});
  const [hideClosed, setHideClosed] = useState(() => localStorage.getItem('admin_hide_closed') === '1');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const json = await adminFetch('/api/admin');
      if (json.error) throw new Error(json.error);
      setData(json);
      setLastRefresh(new Date());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const connectWs = useCallback(() => {
    if (reconnectRef.current) clearTimeout(reconnectRef.current);

    const ws = new WebSocket(`wss://roulette-api.rushelsilvester.workers.dev/api/admin/ws?token=Fifastreet09`);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      setError('');
    };

    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg.type === 'state') {
          setData(msg.data);
          setLastRefresh(new Date());
          setLoading(false);
        }
      } catch {}
    };

    ws.onclose = () => {
      setConnected(false);
      wsRef.current = null;
      reconnectRef.current = setTimeout(connectWs, 2000);
    };

    ws.onerror = () => {
      setError('WebSocket-Fehler, reconnecting...');
    };
  }, []);

  useEffect(() => {
    if (!authed) return;
    setLoading(true);
    connectWs();
    return () => {
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (wsRef.current) {
        wsRef.current.onclose = null; // prevent reconnect on unmount
        wsRef.current.close();
      }
    };
  }, [authed, connectWs]);

  function handleLogin(e) {
    e.preventDefault();
    if (pw === 'Fifastreet09') {
      sessionStorage.setItem('admin_auth', '1');
      setAuthed(true);
    } else {
      setPwError('Falsches Passwort');
    }
  }

  function setAction(key, val) { setActionLoading(p => ({ ...p, [key]: val })); }

  async function closeRoom(code) {
    if (!window.confirm(`Raum ${code} wirklich schliessen?`)) return;
    setAction(`close-${code}`, true);
    const res = await adminFetch(`/api/admin/rooms/${code}/close`, 'POST');
    setAction(`close-${code}`, false);
    if (res.error) alert('Fehler: ' + res.error);
    else fetchData();
  }

  async function kickPlayer(code, playerId, name) {
    if (!window.confirm(`${name} aus Raum ${code} rauswerfen?`)) return;
    setAction(`kick-${playerId}`, true);
    const res = await adminFetch(`/api/admin/rooms/${code}/players/${playerId}/kick`, 'POST');
    setAction(`kick-${playerId}`, false);
    if (res.error) alert('Fehler: ' + res.error);
    else fetchData();
  }

  function takeOverRoom(code, dealerToken) {
    if (!window.confirm(`Raum ${code} als Dealer übernehmen? Stelle sicher dass das ursprüngliche Dealer-iPad nicht mehr aktiv ist.`)) return;
    window.open(`${DEALER_APP_URL}?room=${code}&token=${dealerToken}`, '_blank');
  }

  function openAddForm(code, startBalance) {
    setAddForm(p => ({
      ...p,
      [code]: { open: true, name: '', balance: String(startBalance), loading: false, result: null, error: '' },
    }));
    setExpanded(p => ({ ...p, [code]: true }));
  }

  function closeAddForm(code) {
    setAddForm(p => ({ ...p, [code]: { ...p[code], open: false, result: null } }));
  }

  async function submitAddPlayer(code) {
    const f = addForm[code];
    if (!f?.name?.trim()) return;
    setAddForm(p => ({ ...p, [code]: { ...p[code], loading: true, error: '' } }));
    const res = await adminFetch(`/api/admin/rooms/${code}/checkin`, 'POST', {
      name: f.name.trim(),
      balance: parseInt(f.balance, 10),
    });
    if (res.error) {
      setAddForm(p => ({ ...p, [code]: { ...p[code], loading: false, error: res.error } }));
    } else {
      setAddForm(p => ({ ...p, [code]: { ...p[code], loading: false, result: res } }));
      fetchData();
    }
  }

  function openAdjustForm(playerId) {
    setAdjustForm(p => ({ ...p, [playerId]: { open: true, amount: '', loading: false, error: '' } }));
  }

  function closeAdjustForm(playerId) {
    setAdjustForm(p => ({ ...p, [playerId]: { open: false, amount: '', loading: false, error: '' } }));
  }

  async function submitAdjust(code, playerId) {
    const f = adjustForm[playerId];
    const amount = parseInt(f?.amount, 10);
    if (!amount || isNaN(amount)) return;
    setAdjustForm(p => ({ ...p, [playerId]: { ...p[playerId], loading: true, error: '' } }));
    const res = await adminFetch(`/api/admin/rooms/${code}/players/${playerId}/adjust`, 'POST', { amount });
    if (res.error) {
      setAdjustForm(p => ({ ...p, [playerId]: { ...p[playerId], loading: false, error: res.error } }));
    } else {
      setAdjustForm(p => ({ ...p, [playerId]: { open: false, amount: '', loading: false, error: '' } }));
      fetchData();
    }
  }

  if (!authed) {
    return (
      <div className="adm-login">
        <div className="adm-login-box">
          <div className="adm-login-logo">🎰</div>
          <h1 className="adm-login-title">Admin Dashboard</h1>
          <p className="adm-login-sub">Roulette Casino Monitor</p>
          <form onSubmit={handleLogin} className="adm-login-form">
            <input
              type="password"
              className="adm-login-input"
              placeholder="Passwort"
              value={pw}
              onChange={e => setPw(e.target.value)}
              autoFocus
            />
            {pwError && <p className="adm-login-error">{pwError}</p>}
            <button type="submit" className="adm-login-btn">ANMELDEN</button>
          </form>
        </div>
      </div>
    );
  }

  const visibleRooms = data?.rooms?.filter(r => hideClosed ? r.phase !== 'closed' : true) ?? [];

  return (
    <div className="adm">
      <header className="adm-header">
        <div className="adm-header-left">
          <span className="adm-header-logo">🎰</span>
          <div>
            <h1 className="adm-header-title">Admin Dashboard</h1>
            <p className="adm-header-sub">Roulette Casino Monitor</p>
          </div>
        </div>
        <div className="adm-header-right">
          <label className="adm-toggle-label">
            <input type="checkbox" checked={hideClosed} onChange={e => {
              setHideClosed(e.target.checked);
              localStorage.setItem('admin_hide_closed', e.target.checked ? '1' : '0');
            }} />
            Geschlossene ausblenden
          </label>
          <span className={`adm-live-badge ${connected ? 'adm-live-on' : 'adm-live-off'}`}>
            {connected ? '● Live' : '○ Getrennt'}
          </span>
          {lastRefresh && (
            <span className="adm-refresh-time">{lastRefresh.toLocaleTimeString('de-CH')}</span>
          )}
          <button className="adm-refresh-btn" onClick={fetchData} disabled={loading}>
            {loading ? '⟳' : '⟳ Aktualisieren'}
          </button>
          <button className="adm-logout-btn" onClick={() => {
            sessionStorage.removeItem('admin_auth');
            setAuthed(false);
          }}>Abmelden</button>
        </div>
      </header>

      {error && <div className="adm-error">Fehler: {error}</div>}

      {data && (
        <>
          {/* Summary — only real chips */}
          <div className="adm-summary">
            <div className="adm-card">
              <div className="adm-card-value">{data.totalRooms}</div>
              <div className="adm-card-label">Räume</div>
            </div>
            <div className="adm-card">
              <div className="adm-card-value">{data.totalPlayers}</div>
              <div className="adm-card-label">Spieler</div>
            </div>
            <div className="adm-card adm-card-gold">
              <div className="adm-card-value">{data.totalRealChips.toLocaleString('de-CH')}</div>
              <div className="adm-card-label">Echte Chips im Umlauf</div>
            </div>
            <div className="adm-card adm-card-dim">
              <div className="adm-card-value">{data.totalFreeChips.toLocaleString('de-CH')}</div>
              <div className="adm-card-label">Free-to-Play Chips</div>
            </div>
          </div>

          {visibleRooms.length === 0 ? (
            <div className="adm-empty">Keine Räume</div>
          ) : (
            <div className="adm-rooms">
              {visibleRooms.map(room => {
                const isClosed = room.phase === 'closed';
                const isOpen = expanded[room.code];
                const af = addForm[room.code];
                const histOpen = showHistory[room.code];

                return (
                  <div key={room.code} className={`adm-room${isClosed ? ' adm-room-closed' : ''}`}>
                    {/* Room header */}
                    <div className="adm-room-header">
                      <div className="adm-room-header-left" onClick={() => setExpanded(p => ({ ...p, [room.code]: !p[room.code] }))} style={{ cursor: 'pointer', flex: 1 }}>
                        <span className="adm-room-code">{room.code}</span>
                        <span className="adm-room-phase" style={{ background: PHASE_COLORS[room.phase] }}>
                          {PHASE_LABELS[room.phase] || room.phase}
                        </span>
                        <span className="adm-room-level">{LEVEL_LABELS[room.ruleLevel] || room.ruleLevel}</span>
                        <span className="adm-room-round">Runde {room.round}</span>
                        {room.lastResult && !isClosed && (
                          <span className={`adm-room-last-result adm-col-${room.lastResult.color}`}>
                            Letzte: {room.lastResult.number}
                          </span>
                        )}
                        {isClosed && room.closedAt && (
                          <span className="adm-room-closed-time">Geschlossen vor {timeAgo(room.closedAt)}</span>
                        )}
                      </div>
                      <div className="adm-room-header-right">
                        <span className="adm-room-stat">{room.playerCount}/{room.maxPlayers} Spieler</span>
                        {room.realChipsInPlay > 0 && (
                          <span className="adm-room-stat adm-stat-gold">{room.realChipsInPlay.toLocaleString('de-CH')} Chips</span>
                        )}
                        <span className="adm-room-stat">Seit {timeAgo(room.createdAt)}</span>
                        {!isClosed && (
                          <>
                            <button
                              className="adm-action-btn adm-action-takeover"
                              onClick={() => takeOverRoom(room.code, room.dealerToken)}
                              title="Dealer-App in neuem Tab öffnen und Raum übernehmen"
                            >⚡ Übernehmen</button>
                            <button
                              className="adm-action-btn adm-action-add"
                              onClick={() => openAddForm(room.code, room.startBalance)}
                            >+ Spieler</button>
                            <button
                              className="adm-action-btn adm-action-close"
                              disabled={actionLoading[`close-${room.code}`]}
                              onClick={() => closeRoom(room.code)}
                            >Schliessen</button>
                          </>
                        )}
                        <span className="adm-room-toggle" onClick={() => setExpanded(p => ({ ...p, [room.code]: !p[room.code] }))} style={{ cursor: 'pointer' }}>
                          {isOpen ? '▲' : '▼'}
                        </span>
                      </div>
                    </div>

                    {/* Expanded body */}
                    {isOpen && (
                      <div className="adm-room-body">
                        {/* Add player form */}
                        {af?.open && (
                          <div className="adm-add-player">
                            {af.result ? (
                              <div className="adm-add-result">
                                <span className="adm-add-result-name">{af.result.name}</span>
                                <span className="adm-add-result-chips">{af.result.balance} Chips</span>
                                <span className="adm-add-result-pin-label">PIN:</span>
                                <span className="adm-add-result-pin">
                                  {af.result.pin.slice(0, 3)} {af.result.pin.slice(3)}
                                </span>
                                <button className="adm-action-btn adm-action-add" onClick={() => openAddForm(room.code, room.startBalance)}>+ Weiterer</button>
                                <button className="adm-action-btn" onClick={() => closeAddForm(room.code)}>Fertig</button>
                              </div>
                            ) : (
                              <div className="adm-add-form">
                                <span className="adm-add-form-title">Spieler hinzufügen</span>
                                <input
                                  className="adm-add-input"
                                  placeholder="Name"
                                  value={af.name}
                                  onChange={e => setAddForm(p => ({ ...p, [room.code]: { ...p[room.code], name: e.target.value } }))}
                                  onKeyDown={e => e.key === 'Enter' && submitAddPlayer(room.code)}
                                />
                                <input
                                  className="adm-add-input adm-add-input-sm"
                                  type="number"
                                  placeholder="Chips"
                                  value={af.balance}
                                  onChange={e => setAddForm(p => ({ ...p, [room.code]: { ...p[room.code], balance: e.target.value } }))}
                                  onKeyDown={e => e.key === 'Enter' && submitAddPlayer(room.code)}
                                />
                                {af.error && <span className="adm-add-error">{af.error}</span>}
                                <button className="adm-action-btn adm-action-add" disabled={af.loading || !af.name.trim()} onClick={() => submitAddPlayer(room.code)}>
                                  {af.loading ? '...' : 'Hinzufügen'}
                                </button>
                                <button className="adm-action-btn" onClick={() => closeAddForm(room.code)}>Abbrechen</button>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Player table */}
                        {room.players.length === 0 ? (
                          <p className="adm-no-players">Keine Spieler</p>
                        ) : (
                          <table className="adm-table">
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Typ</th>
                                <th>Status</th>
                                <th>Start</th>
                                <th>Aktuell</th>
                                <th>G/V</th>
                                <th>Letzter Gewinn</th>
                                <th>Wetten</th>
                                <th>Auf Tisch</th>
                                <th>Beigetreten</th>
                                {!isClosed && <th></th>}
                              </tr>
                            </thead>
                            <tbody>
                              {room.players.map(p => (
                                <tr key={p.id} className={`${p.kicked || p.checkedOut ? 'adm-row-out' : ''} ${!p.isReal ? 'adm-row-free' : ''}`}>
                                  <td className="adm-player-name">{p.name}</td>
                                  <td>
                                    {p.isReal
                                      ? <span className="adm-badge adm-badge-real">Echte Chips</span>
                                      : <span className="adm-badge adm-badge-free">Free-to-Play</span>
                                    }
                                  </td>
                                  <td>
                                    {p.kicked
                                      ? <span className="adm-badge adm-badge-kicked">Rausgeworfen</span>
                                      : p.checkedOut
                                        ? <span className="adm-badge adm-badge-out">Ausgecheckt</span>
                                        : p.checkedIn
                                          ? <span className="adm-badge adm-badge-in">Eingecheckt</span>
                                          : <span className="adm-badge adm-badge-join">Beigetreten</span>
                                    }
                                  </td>
                                  <td className="adm-num">{p.initialBalance.toLocaleString('de-CH')}</td>
                                  <td className={`adm-num ${p.isReal ? 'adm-num-current' : ''}`}>{p.balance.toLocaleString('de-CH')}</td>
                                  <td className={`adm-num ${p.profit >= 0 ? 'adm-pos' : 'adm-neg'}`}>{fmt(p.profit)}</td>
                                  <td className={`adm-num ${p.lastWinnings > 0 ? 'adm-pos' : ''}`}>
                                    {p.lastWinnings > 0 ? `+${p.lastWinnings}` : '—'}
                                  </td>
                                  <td className="adm-num">{p.betCount || '—'}</td>
                                  <td className="adm-num">{p.betsTotal > 0 ? p.betsTotal : '—'}</td>
                                  <td className="adm-time">{timeAgo(p.joinedAt)}</td>
                                  {!isClosed && (
                                    <td className="adm-actions-cell">
                                      {!p.kicked && !p.checkedOut && (() => {
                                        const af2 = adjustForm[p.id];
                                        return af2?.open ? (
                                          <span className="adm-adjust-form">
                                            <input
                                              className="adm-adjust-input"
                                              type="number"
                                              placeholder="±Chips"
                                              value={af2.amount}
                                              autoFocus
                                              onChange={e => setAdjustForm(prev => ({ ...prev, [p.id]: { ...prev[p.id], amount: e.target.value } }))}
                                              onKeyDown={e => { if (e.key === 'Enter') submitAdjust(room.code, p.id); if (e.key === 'Escape') closeAdjustForm(p.id); }}
                                            />
                                            <button className="adm-action-btn adm-action-add" disabled={af2.loading || !af2.amount} onClick={() => submitAdjust(room.code, p.id)}>OK</button>
                                            <button className="adm-action-btn" onClick={() => closeAdjustForm(p.id)}>✕</button>
                                            {af2.error && <span className="adm-add-error">{af2.error}</span>}
                                          </span>
                                        ) : (
                                          <>
                                            <button className="adm-action-btn adm-action-chips" onClick={() => openAdjustForm(p.id)} title="Chips hinzufügen / abziehen">±Chips</button>
                                            <button
                                              className="adm-kick-btn"
                                              disabled={actionLoading[`kick-${p.id}`]}
                                              onClick={() => kickPlayer(room.code, p.id, p.name)}
                                              title="Rauswerfen"
                                            >✕</button>
                                          </>
                                        );
                                      })()}
                                    </td>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr className="adm-table-total">
                                <td colSpan={3}>Total (Echte Chips)</td>
                                <td className="adm-num">
                                  {room.players.filter(p => p.isReal && !p.kicked).reduce((s, p) => s + p.initialBalance, 0).toLocaleString('de-CH')}
                                </td>
                                <td className="adm-num adm-num-current">
                                  {room.realChipsInPlay.toLocaleString('de-CH')}
                                </td>
                                <td className={`adm-num ${room.players.filter(p => p.isReal).reduce((s,p) => s+p.profit,0) >= 0 ? 'adm-pos' : 'adm-neg'}`}>
                                  {fmt(room.players.filter(p => p.isReal && !p.kicked).reduce((s, p) => s + p.profit, 0))}
                                </td>
                                <td colSpan={!isClosed ? 5 : 4}></td>
                              </tr>
                            </tfoot>
                          </table>
                        )}

                        {/* Spin History */}
                        {room.spinHistory && room.spinHistory.length > 0 && (
                          <div className="adm-history">
                            <div className="adm-history-header" onClick={() => setShowHistory(p => ({ ...p, [room.code]: !p[room.code] }))}>
                              <span className="adm-history-title">Spin-Verlauf ({room.spinHistory.length} Runden)</span>
                              <span className="adm-history-toggle">{histOpen ? '▲' : '▼'}</span>
                            </div>
                            {histOpen && (
                              <div className="adm-history-body">
                                <div className="adm-history-numbers">
                                  {room.spinHistory.map((s, i) => (
                                    <div key={i} className={`adm-hist-num adm-hist-${s.color}`} title={`Runde ${s.round} — ${timeAgo(s.timestamp)}`}>
                                      {s.number}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Bets / last-round results */}
                        {(() => {
                          const ph = room.phase;
                          const isResult = ph === 'result';
                          const playersWithData = room.players.filter(p =>
                            p.betCount > 0 || p.lastResults?.length > 0
                          );
                          if (playersWithData.length === 0) return null;

                          const title = isResult ? `Ergebnis – Runde ${room.round}` : 'Wetten';

                          return (
                            <div className="adm-bets-detail">
                              <h4 className="adm-bets-title">{title}</h4>
                              <div className="adm-bets-grid">
                                {playersWithData.map(p => {
                                  const hasCurrent = p.betCount > 0;
                                  const hasResults = p.lastResults?.length > 0;
                                  // Show results when: in result phase, OR no current bets but old results exist
                                  const useResults = isResult || (!hasCurrent && hasResults);
                                  const betsToShow = useResults ? (p.lastResults || []) : (p.currentBets || []);
                                  const net = useResults ? (p.lastWinnings ?? 0) : null;
                                  const borderCls = net === null ? '' : net > 0 ? ' adm-bets-win' : net < 0 ? ' adm-bets-lose' : '';

                                  return (
                                    <div key={p.id} className={`adm-bets-player${borderCls}`}>
                                      <div className="adm-bets-player-head">
                                        <span className="adm-bets-player-name">{p.name}</span>
                                        {net !== null && (
                                          <span className={`adm-bets-net ${net > 0 ? 'adm-pos' : net < 0 ? 'adm-neg' : ''}`}>
                                            {net > 0 ? '+' : ''}{net}
                                          </span>
                                        )}
                                        {!useResults && (
                                          <span className="adm-bets-chips">{p.betsTotal} Chips</span>
                                        )}
                                      </div>
                                      {betsToShow.map((b, i) => (
                                        <div key={i} className={`adm-bet-item${useResults ? (b.won ? ' adm-bet-won' : ' adm-bet-lost') : ''}`}>
                                          <span className={`adm-bet-indicator${useResults ? (b.won ? ' won' : ' lost') : ''}`} />
                                          <span className="adm-bet-type">{b.type}</span>
                                          {b.numbers?.length > 0 && <span className="adm-bet-nums">[{b.numbers.join(',')}]</span>}
                                          <span className="adm-bet-amount">{b.amount}</span>
                                          {useResults && (
                                            <span className={`adm-bet-payout ${b.won ? 'adm-pos' : 'adm-neg'}`}>
                                              {b.won ? `+${b.winnings}` : `−${b.amount}`}
                                            </span>
                                          )}
                                        </div>
                                      ))}
                                      {!useResults && (
                                        <div className="adm-bets-pending">ausstehend…</div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {!data && !loading && !error && <div className="adm-empty">Lade Daten...</div>}
    </div>
  );
}
